import { Link } from "react-router-dom";
import { Shield, FileText, Bot, Lock, Archive, ShieldAlert } from "lucide-react";
import DisclaimerBand from "@/components/DisclaimerBand";

/**
 * Global footer — non-affiliation disclaimer + legal armor + AI disclosure.
 * Rendered inside DashboardLayout after `<Outlet/>` and on marketing/legal pages.
 */
export default function Footer() {
  return (
    <footer className="border-t-2 border-ink mt-16 bg-secondary/40" data-testid="site-footer">
      <DisclaimerBand />
      <div className="max-w-7xl mx-auto px-6 py-8 grid gap-6 md:grid-cols-4 font-mono text-xs">
        <div>
          <div className="font-heading font-black text-lg mb-2">Checkmate Payroll</div>
          <p className="text-muted-foreground leading-relaxed">
            The all-in-one payroll, HR, and client-billing suite for agencies and small businesses.
          </p>
        </div>
        <div>
          <div className="overline mb-2">Legal</div>
          <ul className="space-y-1">
            <li><Link to="/legal/disclaimer" data-testid="footer-disclaimer" className="hover:underline flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Disclaimer &amp; Non-Affiliation</Link></li>
            <li><Link to="/legal/terms" data-testid="footer-terms" className="hover:underline flex items-center gap-1"><FileText className="w-3 h-3" /> Terms of Service</Link></li>
            <li><Link to="/legal/privacy" data-testid="footer-privacy" className="hover:underline flex items-center gap-1"><Shield className="w-3 h-3" /> Privacy Policy</Link></li>
            <li><Link to="/legal/retention" data-testid="footer-retention" className="hover:underline flex items-center gap-1"><Archive className="w-3 h-3" /> Data Retention Policy</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-2">Security & Compliance</div>
          <ul className="space-y-1 text-muted-foreground">
            <li className="flex items-center gap-1"><Lock className="w-3 h-3" /> SOC 2-compliant infrastructure</li>
            <li className="flex items-center gap-1"><Lock className="w-3 h-3" /> ISO 20022-compliant financials</li>
            <li>Bank data · Fernet AES-128 encrypted</li>
          </ul>
        </div>
        <div>
          <div className="overline mb-2 flex items-center gap-1"><Bot className="w-3 h-3" /> AI Disclosure</div>
          <p className="text-muted-foreground leading-relaxed">
            Checkmate uses AI-assisted automation for the Office Manager (workflow suggestions), Billing Concierge
            (packet grouping), and Checkmate Buddy (support chat). AI outputs are for admin review only — payroll
            decisions and financial numbers are always deterministic and human-approved.
          </p>
        </div>
      </div>
      <div className="border-t-2 border-ink px-6 py-4 flex justify-center">
        <div
          data-testid="security-badge"
          className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-2 border-ink bg-ink text-white px-5 py-2.5 shadow-hard"
        >
          <Lock className="w-3.5 h-3.5 shrink-0" />
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-center">
            Bank-Grade Encryption · 7-Year Data Defense · SOC2 Discipline Built-In
          </span>
        </div>
      </div>
      <div className="border-t border-ink/20 px-6 py-3 text-center font-mono text-[10px] text-muted-foreground">
        © {new Date().getFullYear()} Checkmate Payroll · Built for teams that get paid on time.
      </div>
    </footer>
  );
}
